/**
 * Centralized fetch utility for API calls
 * Handles base URL, headers, authentication token injection, error formatting
 * 
 * @module services/api/client
 * @example
 * ```typescript
 * const data = await apiGet<{ data: Asset[] }>('/api/assets')
 * ```
 */

import type { ApiError } from '@/types/api'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'

/**
 * Get authentication token (from Auth0)
 * This will be set by the useAuth hook when making API calls
 */
let authTokenGetter: (() => Promise<string | null>) | null = null

/**
 * Set the token getter function (called from useAuth hook)
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>): void {
  authTokenGetter = getter
}

/**
 * Check if token getter is available
 */
export function isTokenGetterSet(): boolean {
  return authTokenGetter !== null
}

/**
 * Get current auth token
 */
export async function getAuthToken(): Promise<string | null> {
  if (!authTokenGetter) {
    return null
  }
  return await authTokenGetter()
}

/**
 * Centralized fetch function with error handling
 * 
 * @param endpoint - API endpoint path (e.g., '/api/assets')
 * @param options - Fetch options (method, body, headers, etc.)
 * @returns Promise resolving to response data
 * @throws Error if request fails or response is not ok
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  const token = await getAuthToken()

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }

  // Only set Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers: headers as HeadersInit,
    })

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`
      
      try {
        const errorData: ApiError = await response.json()
        errorMessage = errorData.message || errorMessage
      } catch {
        // If response is not JSON, use status text
      }
      
      const error = new Error(errorMessage)
      // Add status code to error for better handling
      ;(error as any).status = response.status
      throw error
    }

    const data = await response.json()
    return data as T
  } catch (error) {
    // Re-throw if it's already an Error with status
    if (error instanceof Error) {
      // Check for network errors (CORS, network failure, etc.)
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        const networkError = new Error('Lỗi kết nối mạng. Vui lòng kiểm tra kết nối hoặc cấu hình CORS.')
        ;(networkError as any).isNetworkError = true
        throw networkError
      }
      throw error
    }
    throw new Error('An unexpected error occurred')
  }
}

/**
 * GET request helper
 */
export function apiGet<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(endpoint, { ...options, method: 'GET' })
}

/**
 * POST request helper
 */
export function apiPost<T>(endpoint: string, body: unknown, options?: RequestInit): Promise<T> {
  // If body is FormData, don't stringify it - let browser set Content-Type with boundary
  // If body is already a string or Blob, use it as-is
  // Otherwise, stringify as JSON
  let requestBody: BodyInit
  if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
    requestBody = body as BodyInit
  } else {
    requestBody = JSON.stringify(body)
  }

  return apiFetch<T>(endpoint, {
    ...options,
    method: 'POST',
    body: requestBody,
  })
}

/**
 * PUT request helper
 */
export function apiPut<T>(endpoint: string, body: unknown, options?: RequestInit): Promise<T> {
  // If body is FormData, don't stringify it - let browser set Content-Type with boundary
  // If body is already a string or Blob, use it as-is
  // Otherwise, stringify as JSON
  let requestBody: BodyInit
  if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
    requestBody = body as BodyInit
  } else {
    requestBody = JSON.stringify(body)
  }

  return apiFetch<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: requestBody,
  })
}

/**
 * DELETE request helper
 */
export function apiDelete<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(endpoint, { ...options, method: 'DELETE' })
}

